import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import type { ModifierGroup } from '../menu/modifierGroupSchema';
import { modifierGroupSchema } from '../menu/modifierGroupSchema';
import type { Modifier } from '../menu/modifierSchema';
import { modifierSchema } from '../menu/modifierSchema';

export const orderItemModifierSchema = z.object({
  localCreatedAt: z.number(),
  localUpdatedAt: z.number(),
  modifierGroupDefaultModifierUuid: modifierGroupSchema.shape.defaultModifierUuid,
  modifierGroupDisplayName: modifierGroupSchema.shape.displayName,
  modifierGroupModifierType: modifierGroupSchema.shape.modifierType,
  modifierGroupName: modifierGroupSchema.shape.name,
  modifierGroupQtyMax: modifierGroupSchema.shape.qtyMax,
  modifierGroupQtyMin: modifierGroupSchema.shape.qtyMin,
  modifierGroupUuid: z.string(), // Note: Don't use `modifierGroupSchema.shape.uuid` as it uses default value which makes input type optional
  modifierName: modifierSchema.shape.name,
  modifierPrice: modifierSchema.shape.price,
  modifierUuid: z.string(), // Note: Don't use `modifierSchema.shape.uuid` as it uses default value which makes input type optional
  qty: z.number().default(1),
  total: z.number(),
  uuid: z.string().default(() => uuidv4()),
});
export type OrderItemModifier = z.output<typeof orderItemModifierSchema>;

/** Strict schema */
export const orderItemModifierStrictSchema = orderItemModifierSchema.extend({
  modifierGroupUuid: z.guid(),
  modifierUuid: z.guid(),
  uuid: z.guid(),
});

// Factory
export type OrderItemModifierInput = {
  modifier: Modifier;
  modifierGroup: ModifierGroup;
};
export const initOrderItemModifier = ({
  modifier,
  modifierGroup,
}: OrderItemModifierInput): OrderItemModifier => {
  // Ensure modifier belongs to modifier group.
  if (modifier.modifierGroupUuid !== modifierGroup.uuid) {
    throw new Error(
      "initOrderItemModifier(): `modifier` doesn't seem to belong to `modifierGroup`.",
    );
  }

  const now = Date.now();
  return orderItemModifierSchema.decode(
    {
      localCreatedAt: now,
      localUpdatedAt: now,
      modifierGroupDefaultModifierUuid: modifierGroup.defaultModifierUuid,
      modifierGroupDisplayName: modifierGroup.displayName,
      modifierGroupModifierType: modifierGroup.modifierType,
      modifierGroupName: modifierGroup.name,
      modifierGroupQtyMax: modifierGroup.qtyMax,
      modifierGroupQtyMin: modifierGroup.qtyMin,

      // modifier group
      modifierGroupUuid: modifierGroup.uuid,
      modifierName: modifier.name,
      modifierPrice: modifier.price,

      // modifier
      modifierUuid: modifier.uuid,
      /** `total` will be `modifierPrice` * `qty` later when we support `qty` change. */
      total: modifier.price,
      uuid: uuidv4(),
    },
    { reportInput: true },
  );
};
